import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";

import FormRichTextField from "../../components/formFields/FormRichTextField";

import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  createCoreService,
  getCoreServiceByID,
  CoreServiceImageUpload,
  updateCoreService,
} from "./services/coreServices.services";
import { clearCoreServiceDetail } from "./coreServicesSlice";
import { IService } from "../services/servicesModel";
import { getAllActiveService } from "../services/services/services.services";
import { slugify } from "../../_helper/helper";

const CreateCoreService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CoreServiceID = location.state?.CoreServiceID;
  const CoreServiceDetail = useAppSelector(
    (state) => state.coreservice.CoreServiceDetail,
  );
  const loading = useAppSelector((state) => state.coreservice.loading);
  const ServiceList = useAppSelector((state) => state.service.ServiceList);

  useEffect(() => {
    dispatch(getAllActiveService());
    return () => {
      dispatch(clearCoreServiceDetail());
    };
  }, []);
  useEffect(() => {
    if (CoreServiceID) {
      dispatch(getCoreServiceByID(CoreServiceID));
    }
  }, [CoreServiceID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ServicesID: values?.ServicesID,
      CoreService: values?.CoreService,
      URL: values?.URL,
      LongDescription: values?.LongDescription,
      SortDescription: values?.SortDescription,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage &&
      typeof values?.MainImage !== "string" &&
      Array.isArray(values?.MainImage) &&
      values?.MainImage?.length > 0
        ? values.MainImage[0].getRawFile()
        : "";
    const IconImage =
      values?.IconImage &&
      typeof values?.IconImage !== "string" &&
      Array.isArray(values?.IconImage) &&
      values?.IconImage?.length > 0
        ? values.IconImage[0].getRawFile()
        : "";

    try {
      if (CoreServiceID) {
        const editPayload = {
          ...formData,
          ID: CoreServiceID,
        };

        const response = await dispatch(updateCoreService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage &&
              typeof values?.MainImage !== "string" &&
              Array.isArray(values?.MainImage) &&
              values?.MainImage?.length > 0) ||
            (values?.IconImage &&
              typeof values?.IconImage !== "string" &&
              Array.isArray(values?.IconImage) &&
              values?.IconImage?.length > 0)
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("IconImage", IconImage);

            ImageData.append("ID", CoreServiceID);
            const imageResponse = await dispatch(
              CoreServiceImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/coreservice");
            } else {
              navigate("/coreservice");
            }
          } else {
            navigate("/coreservice");
          }
        }
      } else {
        const response = await dispatch(createCoreService(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (response?.payload?.Data?.ID &&
              values?.MainImage &&
              typeof values?.MainImage !== "string" &&
              Array.isArray(values?.MainImage) &&
              values?.MainImage?.length > 0) ||
            (values?.IconImage &&
              typeof values?.IconImage !== "string" &&
              Array.isArray(values?.IconImage) &&
              values?.IconImage?.length > 0)
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("IconImage", IconImage);

            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              CoreServiceImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/coreservice");
            } else {
              navigate("/coreservice");
            }
          } else {
            navigate("/coreservice");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={CoreServiceDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {CoreServiceID
                        ? "Update Core Service"
                        : "Create Core Service"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="CoreService"
                      label="Core Service Name"
                      placeholder="i.e. Commercial Solutions"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="i.e.commercial-solutions"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="ServicesID"
                      label="Service"
                      placeholder="Select Service"
                      astrike={true}
                      component={FormSelectionField}
                      options={ServiceList.map((service: IService) => {
                        return {
                          value: service.ID,
                          label: service.ServiceName,
                        };
                      })}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="SortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      astrike={true}
                      style={{ height: 250 }}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={2}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      component={FormRichTextField}
                      style={{ height: 250 }}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Image"
                      component={FormImageUploadMorden}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`IconImage`}
                      label="Icon Image"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Image"
                      component={FormImageUploadMorden}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{
                      marginTop: 20,
                    }}
                  >
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CoreServiceID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"secondary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/coreservice")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCoreService;
